import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { setFlashToast } from '../../../utils/flashToast';
import * as attorneyGeneralService from '../../../services/attorneyGeneralCaseRequests';
import type { AttorneyGeneralWizardDraft } from '../../../types/attorneyGeneralCaseRequest';
import type { AttorneyGeneralWizardStep } from './attorneyGeneralConstants';
import { useWizardCancelNavigation } from '../useWizardCancelNavigation';
import { useWizardNavLabels } from '../useWizardNavLabels';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export function AttorneyGeneralWizardActions({
  draft,
  setDraft,
  currentStep,
  onBack,
  onNext,
  nextLabel,
  showReview = false,
  showSubmit = false,
  submitting = false,
  onSubmit,
  validateBeforeSave,
  reviewMode = false,
}: {
  draft: AttorneyGeneralWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<AttorneyGeneralWizardDraft>>;
  currentStep: AttorneyGeneralWizardStep;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  showReview?: boolean;
  showSubmit?: boolean;
  submitting?: boolean;
  onSubmit?: () => void;
  validateBeforeSave?: () => boolean;
  reviewMode?: boolean;
}) {
  const nav = useNavigate();
  const { roleCode } = useRole();
  const cancelWizard = useWizardCancelNavigation();
  const labels = useWizardNavLabels();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const actionsBusy = saving || submitting;

  const saveDraft = async () => {
    if (validateBeforeSave && !validateBeforeSave()) {
      // setFlashToast({ message: labels.fillRequiredFields, variant: 'error' });
      return;
    }

    setSaving(true);
    const r = await attorneyGeneralService.saveAttorneyGeneralRequest(draft, {
      isDraft: true,
      currentStep,
    });
    setSaving(false);
    if (r.success) {
      setDraft((d) => ({
        ...d,
        draftRequestId: r.data.requestId,
        referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
      }));
      setFlashToast({ message: labels.draftSaved, variant: 'success' }, { persist: true });
      nav(tasksListPathForRole(roleCode));
    } else {
      setFlashToast({ message: r.message ?? labels.draftSaveFailed, variant: 'error' });
    }
  };

  return (
    <>
      <div className={`ccr-actions${reviewMode ? ' ccr-actions--review' : ''}`}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="ccr-btn-ol" type="button" onClick={() => setCancelOpen(true)} disabled={actionsBusy}>
            {labels.cancel}
          </button>
          {!reviewMode ? (
            <button className="ccr-btn-ol ccr-btn-draft" type="button" onClick={() => void saveDraft()} disabled={actionsBusy}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              {saving ? labels.saving : labels.saveAsDraft}
            </button>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onBack ? (
            <button className="ccr-btn-ol" type="button" onClick={onBack} disabled={actionsBusy}>
              {labels.back}
            </button>
          ) : null}
          {showReview ? (
            <button className="ccr-btn-pr" type="button" onClick={onNext} disabled={actionsBusy}>
              {labels.reviewBeforeSubmission}
            </button>
          ) : showSubmit ? (
            <button
              className={reviewMode ? 'ccr-btn-submit-review' : 'btn-submit'}
              type="button"
              onClick={onSubmit}
              disabled={actionsBusy}
              aria-busy={submitting}
            >
              {reviewMode ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              ) : null}
              {submitting ? labels.submitting : labels.submitRequest}
            </button>
          ) : onNext ? (
            <button className="ccr-btn-pr" type="button" onClick={onNext} disabled={actionsBusy}>
              {nextLabel ?? labels.next}
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelOpen}
        title={labels.cancelCaseRequest}
        message={labels.cancelConfirm}
        onClose={() => setCancelOpen(false)}
        onConfirm={cancelWizard}
      />
    </>
  );
}
